import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type {
  DisruptionDetail,
  DisruptionFlightKind,
  DisruptionListRow,
  DisruptionOverview,
  IDisruptionFlightStatus,
  IPage,
} from '@/lib/disruptions/types';
import {
  CARE_ENTITLEMENTS_SEGMENT,
  careEntitlementsKey,
  caseDelayKey,
  disruptionDelaysKey,
} from './keys';
import { isEscalated } from './format';
import type {
  FlightDelayChangedEvent,
  FlightDelayPhase,
  FlightEscalatedEvent,
  ICareEntitlementView,
  ICaseFlightDelay,
  ICaseFlightDelayUpdatedPayload,
  ICaseFlightEscalatedPayload,
  IRebookingHold,
} from './types';

/**
 * The flight-delay half of the central WS merge (U1) — called from
 * `lib/ws/apply-event.ts` for `FlightDelayChanged@v1` / `FlightEscalated@v1`, and by the
 * declaration write with the timeline it answered. Live state is PUSHED (frontend-
 * conventions §8.1): nothing here starts a timer or a poll.
 *
 * What each push touches:
 *   1. the case's timeline (`caseDelayKey`) — patched with every field the payload
 *      carries, guarded by `revision` (a redelivered or out-of-order revision is a
 *      no-op, so at-least-once delivery lands on identical state). The payload has no
 *      history row, so an OPEN delay panel (an active query) re-reads the case once per
 *      revision; a closed one only flips stale. Never seeded from a partial payload.
 *      The payload's `rebookingHold` (active, released at / by name) is the hold's
 *      truth when present, so "Rebooking released by <name>" shows live;
 *   2. the disruption's timelines (`disruptionDelaysKey`, the header badge) — the same
 *      patch on the case's entry; a case the list does not hold yet (a NEW timeline)
 *      marks the list stale instead of inventing a partial row;
 *   3. `flightStatus@v1` on the disruption's detail, overview row and list rows — a
 *      MONOTONIC fold (severity, worst delay, escalated), exactly the facts the BFF's
 *      summary fold would compute, so a push can never contradict the next read. The
 *      one exception is `outOfPolicy` (delay-limits), which turns OFF too: set by a
 *      flagged push, re-derived from the disruption's cached timelines when one clears —
 *      or, without them, the rows still showing it re-read once (`patchWithFlag`);
 *   4. the case's delay-care entitlements, when the push earned a new meal tier (the
 *      wallet records it asynchronously; there is no wallet push for a PENDING row).
 */

/* ── flightStatus@v1 fold ──────────────────────────────────────────────────── */

const SEVERITY: Readonly<Record<DisruptionFlightKind, number>> = { DELAY: 1, DIVERTED: 2, CANCELLED: 3 };

interface IFlightFact {
  currentKind: DisruptionFlightKind;
  phase: FlightDelayPhase;
  maxDelayMinutes: number;
  graceUntil?: string;
  releasedAt?: string;
  /** The disruption's out-of-policy flag after this fact; `undefined` = keep the row's. */
  outOfPolicy?: boolean | undefined;
}

/**
 * Fold one flight's fact into a disruption's `flightStatus@v1`. Monotonic like the BFF's
 * fold over the cases summary (`delay.flight-status.ts`): the kind only climbs the
 * lattice DELAY < DIVERTED < CANCELLED (an escalated delay counts as CANCELLED), the
 * worst delay only grows, `escalated` never un-sets. `graceUntil` is the EARLIEST open
 * grace window: an open one can only pull it earlier; the release of the window the row
 * shows drops it (another flight's later window returns with the next read).
 * `outOfPolicy` is NOT monotonic: the fact's value when it carries one, else the row's.
 */
export function foldFlightStatus(
  prev: IDisruptionFlightStatus | undefined,
  fact: IFlightFact,
): IDisruptionFlightStatus {
  const escalatedNow = isEscalated(fact.phase);
  const incoming: DisruptionFlightKind = escalatedNow ? 'CANCELLED' : fact.currentKind;
  const kind = prev?.kind && SEVERITY[prev.kind] >= SEVERITY[incoming] ? prev.kind : incoming;
  let graceUntil = prev?.graceUntil;
  if (fact.phase === 'ESCALATED_GRACE' && fact.graceUntil && !fact.releasedAt) {
    if (!graceUntil || Date.parse(fact.graceUntil) < Date.parse(graceUntil)) graceUntil = fact.graceUntil;
  } else if (fact.graceUntil && graceUntil === fact.graceUntil && (fact.releasedAt || fact.phase !== 'ESCALATED_GRACE')) {
    graceUntil = undefined;
  }
  const outOfPolicy = fact.outOfPolicy ?? prev?.outOfPolicy === true;
  return {
    kind,
    maxDelayMinutes: Math.max(prev?.maxDelayMinutes ?? 0, fact.maxDelayMinutes),
    escalated: (prev?.escalated ?? false) || escalatedNow,
    ...(graceUntil ? { graceUntil } : {}),
    ...(outOfPolicy ? { outOfPolicy: true as const } : {}),
  };
}

function sameStatus(a: IDisruptionFlightStatus | undefined, b: IDisruptionFlightStatus): boolean {
  return (
    !!a &&
    a.kind === b.kind &&
    a.maxDelayMinutes === b.maxDelayMinutes &&
    a.escalated === b.escalated &&
    a.graceUntil === b.graceUntil &&
    a.outOfPolicy === b.outOfPolicy
  );
}

/** Write the folded status onto every cached row of the disruption (detail, overview, list). */
function patchFlightStatus(qc: QueryClient, disruptionUrn: string, fact: IFlightFact): void {
  const patchRow = <R extends DisruptionListRow>(row: R): R => {
    if (row.urn !== disruptionUrn) return row;
    const next = foldFlightStatus(row.flightStatus, fact);
    return sameStatus(row.flightStatus, next) ? row : { ...row, flightStatus: next };
  };
  if (qc.getQueryData<DisruptionDetail>(['disruptions', disruptionUrn]) !== undefined) {
    qc.setQueryData<DisruptionDetail>(['disruptions', disruptionUrn], (d) => (d ? patchRow(d) : d));
  }
  if (qc.getQueryData<DisruptionOverview>(['disruptions', 'overview']) !== undefined) {
    qc.setQueryData<DisruptionOverview>(['disruptions', 'overview'], (o) =>
      o?.rows ? { ...o, rows: o.rows.map(patchRow) } : o,
    );
  }
  // The infinite list: `['disruptions', <filters object>]` (useInfiniteList).
  qc.setQueriesData<InfiniteData<IPage<DisruptionListRow>>>(
    {
      predicate: (q) =>
        q.queryKey.length === 2 &&
        q.queryKey[0] === 'disruptions' &&
        typeof q.queryKey[1] === 'object' &&
        q.queryKey[1] !== null,
    },
    (data) =>
      data?.pages
        ? { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.map(patchRow) })) }
        : data,
  );
}

type IRowLike = { urn?: string; flightStatus?: IDisruptionFlightStatus };

/** Every cached row of the disruption that still shows `outOfPolicy` re-reads once (event-driven, never a poll). */
function rereadFlagged(qc: QueryClient, disruptionUrn: string): void {
  const flagged = (row: IRowLike | undefined): boolean =>
    row?.urn === disruptionUrn && row.flightStatus?.outOfPolicy === true;
  void qc.invalidateQueries({
    predicate: ({ queryKey, state }) => {
      if (queryKey.length !== 2 || queryKey[0] !== 'disruptions') return false;
      const d = state.data as (IRowLike & { rows?: IRowLike[]; pages?: Array<{ items: IRowLike[] }> }) | undefined;
      return flagged(d) || !!d?.rows?.some(flagged) || !!d?.pages?.some((pg) => pg.items.some(flagged));
    },
  });
}

/**
 * `fact` onto the disruption's rows, with the disruption-level `outOfPolicy` once one
 * case's flag is `flagged` (`wasFlagged` = the flag the caches held before; `null` =
 * unknown):
 *   - flagged → on;
 *   - known clear before and still clear → kept: this push changed nothing;
 *   - cleared (or unknown) → re-derived from the disruption's timelines when the cache
 *     holds them, this case included; otherwise kept, and the rows that still show the
 *     flag re-read once AFTER the patch — only the BFF's fold knows whether another
 *     flight keeps it.
 */
function patchWithFlag(
  qc: QueryClient,
  disruptionUrn: string,
  caseUrn: string,
  fact: IFlightFact,
  flagged: boolean,
  wasFlagged: boolean | null,
): void {
  const list = qc.getQueryData<ICaseFlightDelay[]>(disruptionDelaysKey(disruptionUrn));
  const mayClear = !flagged && wasFlagged !== false;
  const derivable = mayClear && !!list?.some((d) => d.caseUrn === caseUrn);
  // `undefined` keeps the row's flag.
  const outOfPolicy = flagged ? true : derivable ? !!list?.some((d) => !!d.outOfPolicy) : undefined;
  patchFlightStatus(qc, disruptionUrn, { ...fact, outOfPolicy });
  if (mayClear && !derivable) rereadFlagged(qc, disruptionUrn);
}

/** The freshest timeline the caches hold for a case (its own read or the disruption's list entry). */
function knownDelay(qc: QueryClient, disruptionUrn: string, caseUrn: string): ICaseFlightDelay | undefined {
  const own = qc.getQueryData<ICaseFlightDelay | null>(caseDelayKey(caseUrn)) ?? undefined;
  const listed = qc.getQueryData<ICaseFlightDelay[]>(disruptionDelaysKey(disruptionUrn))?.find((d) => d.caseUrn === caseUrn);
  return own && listed && listed.revision > own.revision ? listed : (own ?? listed);
}

/* ── the case's timeline ───────────────────────────────────────────────────── */

const HOLD_LIFTED: ReadonlySet<FlightDelayPhase> = new Set(['DEPARTED', 'RECOVERED', 'ESCALATED_RELEASED']);

/**
 * The hold a revision describes. With the payload's `rebookingHold` (B1 release
 * follow-up) that is the truth: `active` as sent, and a manual release (`releasedAt` +
 * `releasedByName`) recorded on `releasedBy`. The payload carries only the name and the
 * instant — the releaser's URN, reason and groups arrive with the next read (the open
 * panel re-reads per revision; the disruption list re-reads once per new release), so
 * until then they are empty, exactly as `mergeEscalation` leaves its lists. A release
 * the cache already holds (same `at`) keeps everything it knows. Without the field
 * (an older producer) the phase decides, as before: DEPARTED / RECOVERED /
 * ESCALATED_RELEASED lift it (TDS §5.1).
 */
export function mergeHold(prev: IRebookingHold, p: ICaseFlightDelayUpdatedPayload): IRebookingHold {
  const ph = p.rebookingHold;
  if (!ph) return HOLD_LIFTED.has(p.phase) ? { ...prev, active: false } : prev;
  const next: IRebookingHold = { ...prev, active: ph.active };
  if (ph.releasedAt) {
    const known = prev.releasedBy && prev.releasedBy.at === ph.releasedAt ? prev.releasedBy : undefined;
    const name = ph.releasedByName ?? known?.name;
    next.releasedBy = {
      urn: known?.urn ?? '',
      at: ph.releasedAt,
      reason: known?.reason ?? '',
      subCaseUrns: known?.subCaseUrns ?? [],
      ...(name ? { name } : {}),
    };
  }
  return next;
}

/** The revision records a manual release the cached timeline does not know yet. */
function isNewRelease(prev: ICaseFlightDelay, p: ICaseFlightDelayUpdatedPayload): boolean {
  const at = p.rebookingHold?.releasedAt;
  return !!at && prev.rebookingHold?.releasedBy?.at !== at;
}

/**
 * A revision's payload over the cached timeline, or `null` when the cache already holds
 * that revision or a newer one. Optional fields follow the payload (absent ⇒ unset: the
 * producer emits the whole state) — `outOfPolicy` included, as-is. The hold is lifted
 * when the phase lifts it (TDS §5.1); history, the care amounts and the escalation lists
 * arrive with the re-read.
 */
export function mergeDelayPayload(
  prev: ICaseFlightDelay,
  p: ICaseFlightDelayUpdatedPayload,
): ICaseFlightDelay | null {
  if (typeof p.revision !== 'number' || p.revision <= prev.revision) return null;
  const rest: ICaseFlightDelay = { ...prev };
  delete rest.estimatedDepartureAt;
  delete rest.actualDepartureAt;
  delete rest.outOfPolicy;
  // The meal timing is the payload's whole state too (absent ⇒ unset): the next tier issued,
  // the flight left or the delay shrank must not leave a stale «comida N a las HH:MM».
  const care = { ...prev.care };
  delete care.mealTiming;
  delete care.forecastMealTiers;
  delete care.nextMealTier;
  delete care.nextMealUpToTier;
  delete care.nextMealAt;
  const escalation =
    prev.escalation && p.escalation && prev.escalation.seq === p.escalation.seq
      ? {
          ...prev.escalation,
          graceUntil: p.escalation.graceUntil,
          ...(p.escalation.releasedAt ? { releasedAt: p.escalation.releasedAt } : {}),
        }
      : prev.escalation;
  return {
    ...rest,
    ...(p.disruptionUrn ? { disruptionUrn: p.disruptionUrn } : {}),
    currentKind: p.currentKind,
    phase: p.phase,
    scheduledDepartureAt: p.scheduledDepartureAt,
    ...(p.estimatedDepartureAt ? { estimatedDepartureAt: p.estimatedDepartureAt } : {}),
    ...(p.actualDepartureAt ? { actualDepartureAt: p.actualDepartureAt } : {}),
    delayMinutes: p.delayMinutes,
    maxDelayMinutes: Math.max(prev.maxDelayMinutes, p.maxDelayMinutes),
    care: { ...care, ...p.care },
    rebookingHold: mergeHold(prev.rebookingHold, p),
    ...(escalation ? { escalation } : {}),
    ...(p.outOfPolicy ? { outOfPolicy: p.outOfPolicy } : {}),
    revision: p.revision,
  };
}

/** Upsert a whole timeline into the disruption's list (by caseUrn), when the list is cached. */
function upsertInList(qc: QueryClient, disruptionUrn: string, delay: ICaseFlightDelay): void {
  const key = disruptionDelaysKey(disruptionUrn);
  if (qc.getQueryData<ICaseFlightDelay[]>(key) === undefined) return;
  qc.setQueryData<ICaseFlightDelay[]>(key, (rows) => {
    if (!rows) return rows;
    const idx = rows.findIndex((r) => r.caseUrn === delay.caseUrn);
    if (idx < 0) return [...rows, delay];
    const cur = rows[idx]!;
    if (cur.revision > delay.revision) return rows;
    return rows.map((r, i) => (i === idx ? delay : r));
  });
}

/**
 * `FlightDelayChanged@v1` → the case timeline, the disruption's timelines, the rows'
 * `flightStatus`, and (on a new meal tier) the entitlements. See the module comment.
 */
export function applyFlightDelayChanged(qc: QueryClient, event: FlightDelayChangedEvent): void {
  const p = event.payload;
  const caseUrn = event.caseUrn;
  /** Meal tiers the caches knew before this push (`null` = no baseline cached). */
  let knownMealTiers: number | null = null;
  const before = knownDelay(qc, event.disruptionUrn, caseUrn);

  // 1. the case's own timeline (the delay panel).
  const caseKey = caseDelayKey(caseUrn);
  const cached = qc.getQueryData<ICaseFlightDelay | null>(caseKey);
  if (cached === null) {
    // We read "no timeline" — this push says one exists now: re-read it.
    knownMealTiers = 0;
    void qc.invalidateQueries({ queryKey: caseKey, exact: true });
  } else if (cached) {
    knownMealTiers = cached.care.mealTiers;
    const next = mergeDelayPayload(cached, p);
    if (next) {
      qc.setQueryData<ICaseFlightDelay | null>(caseKey, next);
      // The payload has no history row: an OPEN panel re-reads once per revision.
      void qc.invalidateQueries({ queryKey: caseKey, exact: true });
    }
  }

  // 2. the disruption's timelines (the header badge + flight picker).
  const listKey = disruptionDelaysKey(event.disruptionUrn);
  const list = qc.getQueryData<ICaseFlightDelay[]>(listKey);
  if (list !== undefined) {
    const entry = list.find((d) => d.caseUrn === caseUrn);
    if (!entry) {
      // A timeline the list never held (a newly delayed flight): re-read, never invent a row.
      void qc.invalidateQueries({ queryKey: listKey, exact: true });
    } else {
      knownMealTiers ??= entry.care.mealTiers;
      const next = mergeDelayPayload(entry, p);
      if (next) {
        qc.setQueryData<ICaseFlightDelay[]>(listKey, (rows) =>
          rows ? rows.map((r) => (r.caseUrn === caseUrn ? next : r)) : rows,
        );
        // Re-read the list once when the payload cannot say everything about the hold:
        // re-delayed after a departure / recovery with no `rebookingHold` (an older
        // producer — the hold may be re-claimed), or a NEW manual release (the name is
        // already shown; the releaser's URN / reason / groups come with the read).
        const reclaimed = !p.rebookingHold && entry.phase !== 'DELAYED' && p.phase === 'DELAYED';
        if (reclaimed || isNewRelease(entry, p)) {
          void qc.invalidateQueries({ queryKey: listKey, exact: true });
        }
      }
    }
  }

  // 3. flightStatus@v1 on the disruption's rows — idempotent fold, safe on replays. The
  //    out-of-policy flag is the freshest the caches now hold (a stale replay changes nothing).
  const after = knownDelay(qc, event.disruptionUrn, caseUrn);
  patchWithFlag(
    qc,
    event.disruptionUrn,
    caseUrn,
    {
      currentKind: p.currentKind,
      phase: p.phase,
      maxDelayMinutes: p.maxDelayMinutes,
      ...(p.escalation?.graceUntil ? { graceUntil: p.escalation.graceUntil } : {}),
      ...(p.escalation?.releasedAt ? { releasedAt: p.escalation.releasedAt } : {}),
    },
    after ? !!after.outOfPolicy : !!p.outOfPolicy,
    before ? !!before.outOfPolicy : null,
  );

  // 4. a newly earned meal tier → the wallet records a new entitlement row. With no
  //    baseline cached, any owed meal counts; only an OPEN entitlements list re-reads.
  if (p.care.mealTiers > (knownMealTiers ?? 0)) {
    void qc.invalidateQueries({ queryKey: careEntitlementsKey(caseUrn), exact: true });
  }
}

/** The escalation of `p` over a cached timeline, or `null` when it is already known. */
export function mergeEscalation(prev: ICaseFlightDelay, p: ICaseFlightEscalatedPayload): ICaseFlightDelay | null {
  if (typeof p.escalationSeq !== 'number') return null;
  if (prev.escalation && prev.escalation.seq >= p.escalationSeq) return null;
  return {
    ...prev,
    currentKind: 'CANCELLED',
    phase: prev.phase === 'DELAYED' ? 'ESCALATED_GRACE' : prev.phase,
    escalation: {
      seq: p.escalationSeq,
      escalatedAt: p.escalatedAt,
      delayMinutesAtEscalation: p.delayMinutesAtEscalation,
      graceMinutes: p.graceMinutes,
      graceUntil: p.graceUntil,
      reassignedSubCaseUrns: [],
      releasedSubCaseUrns: [],
      conflictSubCaseUrns: [],
    },
  };
}

/**
 * `FlightEscalated@v1` — a delayed flight became a cancellation. Writes the fact into
 * the timeline(s) and the rows' `flightStatus` (kind CANCELLED, escalated, grace). The
 * alert and the countdown are slice U5's (M2); the revision the same transaction
 * produced arrives as a `FlightDelayChanged` and brings the rest.
 */
export function applyFlightEscalated(qc: QueryClient, event: FlightEscalatedEvent): void {
  const p = event.payload;
  const caseKey = caseDelayKey(event.caseUrn);
  const cached = qc.getQueryData<ICaseFlightDelay | null>(caseKey);
  if (cached) {
    const next = mergeEscalation(cached, p);
    if (next) qc.setQueryData<ICaseFlightDelay | null>(caseKey, next);
  }
  const listKey = disruptionDelaysKey(event.disruptionUrn);
  if (qc.getQueryData<ICaseFlightDelay[]>(listKey) !== undefined) {
    qc.setQueryData<ICaseFlightDelay[]>(listKey, (rows) =>
      rows?.map((r) => (r.caseUrn === event.caseUrn ? (mergeEscalation(r, p) ?? r) : r)),
    );
  }
  patchFlightStatus(qc, event.disruptionUrn, {
    currentKind: 'CANCELLED',
    phase: 'ESCALATED_GRACE',
    maxDelayMinutes: p.delayMinutesAtEscalation,
    graceUntil: p.graceUntil,
  });
}

/**
 * A whole timeline the server answered (a declaration's `delay`) → the same caches the
 * pushes keep, so the operator who declared sees it at once even before the WS echo
 * (which then lands as a no-op: same revision).
 */
export function applyDelaySnapshot(qc: QueryClient, delay: ICaseFlightDelay, disruptionUrn?: string): void {
  const caseKey = caseDelayKey(delay.caseUrn);
  const cached = qc.getQueryData<ICaseFlightDelay | null>(caseKey);
  const urn = disruptionUrn ?? delay.disruptionUrn;
  const before = urn ? knownDelay(qc, urn, delay.caseUrn) : undefined;
  if (!cached || cached.revision <= delay.revision) qc.setQueryData(caseKey, delay);
  if (!urn) return;
  upsertInList(qc, urn, delay);
  const after = knownDelay(qc, urn, delay.caseUrn) ?? delay;
  patchWithFlag(
    qc,
    urn,
    delay.caseUrn,
    {
      currentKind: delay.currentKind,
      phase: delay.phase,
      maxDelayMinutes: delay.maxDelayMinutes,
      ...(delay.escalation?.graceUntil ? { graceUntil: delay.escalation.graceUntil } : {}),
      ...(delay.escalation?.releasedAt ? { releasedAt: delay.escalation.releasedAt } : {}),
    },
    !!after.outOfPolicy,
    before ? !!before.outOfPolicy : null,
  );
}

/**
 * A `CareChanged` push (the wallet issued / adjusted a group's card) may be an
 * entitlement being APPLIED: mark stale every cached entitlements list that holds the
 * group, so an open one re-reads once (closed ones fetch nothing). Push-driven — the
 * wallet has no entitlement event of its own.
 */
export function invalidateEntitlementsOfGroup(qc: QueryClient, subCaseUrn: string): void {
  void qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === 'cases' &&
      q.queryKey[2] === CARE_ENTITLEMENTS_SEGMENT &&
      Array.isArray(q.state.data) &&
      (q.state.data as ICareEntitlementView[]).some((e) => e?.subCaseUrn === subCaseUrn),
  });
}

/* ── WS envelope guards ────────────────────────────────────────────────────── */

function isObj(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** `FlightDelayChanged` as the merge indexes it: room + case URNs and a revisioned payload of that case. */
export function isFlightDelayChanged(v: Record<string, unknown>): boolean {
  const p = v.payload;
  return (
    typeof v.disruptionUrn === 'string' &&
    typeof v.caseUrn === 'string' &&
    isObj(p) &&
    p.caseUrn === v.caseUrn &&
    typeof p.revision === 'number' &&
    typeof p.phase === 'string' &&
    typeof p.currentKind === 'string' &&
    typeof p.scheduledDepartureAt === 'string' &&
    typeof p.delayMinutes === 'number' &&
    typeof p.maxDelayMinutes === 'number' &&
    isObj(p.care) &&
    typeof (p.care as Record<string, unknown>).mealTiers === 'number' &&
    (p.rebookingHold === undefined || isHoldSummary(p.rebookingHold)) &&
    (p.outOfPolicy === undefined ||
      (isObj(p.outOfPolicy) && typeof p.outOfPolicy.delayMinutes === 'number' && typeof p.outOfPolicy.limitMinutes === 'number'))
  );
}

/** The payload's optional `rebookingHold`: `active` boolean, the release facts strings when present. */
function isHoldSummary(v: unknown): boolean {
  if (!isObj(v) || typeof v.active !== 'boolean') return false;
  return (
    (v.releasedAt === undefined || typeof v.releasedAt === 'string') &&
    (v.releasedByName === undefined || typeof v.releasedByName === 'string')
  );
}

/** `FlightEscalated` as the merge indexes it: room + case URNs and a sequenced escalation of that case. */
export function isFlightEscalated(v: Record<string, unknown>): boolean {
  const p = v.payload;
  return (
    typeof v.disruptionUrn === 'string' &&
    typeof v.caseUrn === 'string' &&
    isObj(p) &&
    p.caseUrn === v.caseUrn &&
    typeof p.escalationSeq === 'number' &&
    typeof p.graceUntil === 'string' &&
    typeof p.delayMinutesAtEscalation === 'number'
  );
}
